/** The record page for a series: a setting, product line, adventure series or collection. */
import { Link, useParams } from 'react-router'
import { BookShelf, SeriesShelf, ShelfSkeleton } from '../components/Cards'
import { Chips, Empty, ErrorState, Loading, Pager, usePage } from '../components/Bits'
import {
  Attributions,
  ExternalIds,
  Facts,
  GapList,
  LinkList,
  MergedRedirect,
  Prose,
  RecordHeader,
  type Crumb,
} from '../components/Record'
import { qs } from '../lib/api'
import { plural } from '../lib/format'
import { label, SERIES_KIND_LABELS } from '../lib/labels'
import { useQuery } from '../lib/query'
import { useTitle } from '../lib/useTitle'
import type { BookCard, Page, SeriesDetail } from '../lib/types'

export function RefLinks({ path, refs }: { path: string; refs: Array<{ id?: string; name: string }> }) {
  return (
    <ul className="inline-list">
      {refs.map((r) => (
        <li key={r.id ?? r.name}>{r.id ? <Link to={`/${path}/${r.id}`}>{r.name}</Link> : r.name}</li>
      ))}
    </ul>
  )
}

export function SeriesPage() {
  const { id = '' } = useParams()
  const page = usePage()
  const { data: s, error, refresh } = useQuery<SeriesDetail>(`/api/v1/series/${id}`)
  const { data: books } = useQuery<Page<BookCard>>(`/api/v1/series/${id}/books${qs({ page, limit: 48 })}`)
  useTitle(s?.doc.name)
  if (error) return <ErrorState error={error} retry={refresh} />
  if (!s) return <Loading label="Loading series" />
  if (s.merged_into) return <MergedRedirect type="series" to={s.merged_into} />
  const d = s.doc
  const crumbs: Crumb[] = [{ label: 'Series & settings', to: '/series' }]
  if (s.parent) crumbs.push({ label: s.parent.name, to: `/series/${s.parent.id}` })
  const range = s.level_range
  return (
    <article className="page record">
      <RecordHeader
        type="series"
        id={s.id}
        crumbs={crumbs}
        title={d.name}
        subtitle={[
          label(SERIES_KIND_LABELS, d.kind),
          d.systems.map((x) => x.name).join(' + '),
          range ? `Levels ${range.min ?? '?'}–${range.max ?? '?'}` : '',
        ]
          .filter(Boolean)
          .join(' · ')}
        cover={s.cover}
        score={s.completeness.score}
        editHref={`/series/${s.id}/edit`}
        historyHref={`/series/${s.id}/history`}
        pending={s.pending_edits}
        pendingHref={`/review?entity=${s.id}`}
        deleted={s.deleted}
      >
        {d.aliases.length > 0 && <p className="record-aliases">Also known as {d.aliases.join(', ')}</p>}
      </RecordHeader>
      <div className="record-body">
        <div className="record-main">
          <Prose text={d.description} />
          <Attributions attributions={d.attributions} />
          <GapList missing={s.completeness.missing} score={s.completeness.score} editHref={`/series/${s.id}/edit`} />
        </div>
        <aside className="record-side">
          <Facts
            facts={[
              {
                label: 'Kind',
                value: <Link to={`/series?kind=${d.kind}`}>{label(SERIES_KIND_LABELS, d.kind)}</Link>,
              },
              {
                label: d.systems.length > 1 ? 'Systems' : 'System',
                value: d.systems.length > 0 && <RefLinks path="systems" refs={d.systems} />,
              },
              {
                label: 'Publishers',
                value: d.publishers.length > 0 && <RefLinks path="publishers" refs={d.publishers} />,
              },
              { label: 'Tags', value: d.tags.length > 0 && <Chips items={d.tags} /> },
              { label: 'Elsewhere', value: d.external_ids.length > 0 && <ExternalIds ids={d.external_ids} /> },
            ]}
          />
          <LinkList title="Links" links={d.urls} />
        </aside>
      </div>
      {s.children.length > 0 && (
        <section className="section">
          <h2 className="section-title">Inside this {label(SERIES_KIND_LABELS, d.kind).toLowerCase()}</h2>
          <SeriesShelf items={s.children} />
        </section>
      )}
      <section className="section">
        <h2 className="section-title">
          Books {books && <span className="section-count">{plural(books.total, 'book')}, in series order</span>}
        </h2>
        {!books ? (
          <ShelfSkeleton count={6} />
        ) : books.items.length === 0 ? (
          <Empty title="No books yet">Add a book and choose this series on it.</Empty>
        ) : (
          <>
            <BookShelf items={books.items} />
            <Pager page={books.page} limit={books.limit} total={books.total} />
          </>
        )}
      </section>
    </article>
  )
}
